import { getDiscoveryTargetForPath } from '@revelio/core/global-ai-assistant/discovery-targets';

import { isAnalyticsEntity } from '../CustomDash/utils/revelio-primary-filter-entity-switch';
import type {
  GlobalAssistantCurrentPageContext,
  GlobalAssistantDiscoveryKind,
  GlobalAssistantEntityType,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';
import { isScreenerV2DashboardPath } from './global-ai-assistant-route-filter-kind';
import { getCurrentPageOptionId } from './global-ai-assistant-standard-page-match';
import { globalAssistantPathsMatch } from './global-assistant-route-filter-path';

export type LastAppliedFilterContext = {
  prompt: string;
  targetPath: string;
  surfaceKind: 'discovery' | 'dashboard';
  discoveryKind?: GlobalAssistantDiscoveryKind;
  entityType?: GlobalAssistantEntityType;
};

const CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX = '__current__:apply-filters:';
const CUSTOM_DASH_CURRENT_PAGE_FILTER_PREFIX =
  '__current__:custom-dash-filter:';

function isCustomDashCurrentPageFilterMatch(
  match: GlobalAssistantSuggestMatch
): boolean {
  return match.pageId.startsWith(CUSTOM_DASH_CURRENT_PAGE_FILTER_PREFIX);
}

export function isCurrentPageFilterApplyMatch(
  match: GlobalAssistantSuggestMatch
): boolean {
  return match.pageId.startsWith(CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX);
}

export function getAlreadyOnPageMessage(
  match?: GlobalAssistantSuggestMatch
): string {
  const pageName = match?.name.trim();
  if (pageName) {
    return `You're already on the ${pageName} page.`;
  }
  return "You're already on that page.";
}

/**
 * Returns true when the given match is a pure "self-link" navigation
 * suggestion — its `navigatePath` already matches the current page and it
 * carries no metadata (`resolvedPromptForApply` or
 * `applyFiltersAfterNavigate`) that would make the click do real work.
 * Synthetic current-page apply chips (which intentionally target the
 * current page to apply filters) are also excluded so they keep rendering.
 */
export function isSelfLinkNavigationMatch(
  match: GlobalAssistantSuggestMatch,
  currentPathname: string
): boolean {
  if (isCurrentPageFilterApplyMatch(match)) {
    return false;
  }
  if (match.resolvedPromptForApply?.trim()) {
    return false;
  }
  // `applyFiltersAfterNavigate` matches still apply discovery filters on
  // click using the user's last prompt (see
  // `getGlobalAssistantPageMatchAction` -> `apply_discovery_filters_after_navigate`),
  // so they are not pure self-links even when targeting the current page.
  if (match.applyFiltersAfterNavigate) {
    return false;
  }
  return globalAssistantPathsMatch(currentPathname, match.navigatePath);
}

/**
 * Drops matches that would render as a redundant self-link suggestion to
 * the current page (e.g. a "Compositions" chip while already on
 * `/company/compositions`). Filter-apply chips for the current page are
 * intentionally preserved so the user can still apply filters in place.
 */
export function filterOutSelfLinkNavigationMatches(
  matches: GlobalAssistantSuggestMatch[],
  currentPathname: string
): GlobalAssistantSuggestMatch[] {
  return matches.filter(
    (match) => !isSelfLinkNavigationMatch(match, currentPathname)
  );
}

function getPageNameFromPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return 'this page';
  }
  const [entity, page] = parts;
  const entityName = isAnalyticsEntity(entity)
    ? entity.charAt(0).toUpperCase() + entity.slice(1)
    : entity;
  if (page === 'screener') {
    return `${entityName} Discovery`;
  }
  if (page) {
    const pageName = page
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${entityName} ${pageName}`;
  }
  return 'this page';
}

function getCurrentPageFilterApplyLabel(pathname: string): string {
  const pageName = getPageNameFromPath(pathname);
  return `Apply filters to ${pageName}`;
}

function looksLikeCustomDashPath(pathname: string): boolean {
  return isAnalyticsEntity(pathname.split('/').filter(Boolean)[0]);
}

function isDataDictionaryPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] === 'data-dictionary';
}

function isNormalDashboardFilterPath(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  const [entity, page] = parts;
  if (page === undefined || page === 'screener') {
    return false;
  }
  return isAnalyticsEntity(entity);
}

function isCurrentPageFilterApplyPath(pathname: string): boolean {
  return (
    isNormalDashboardFilterPath(pathname) || isScreenerV2DashboardPath(pathname)
  );
}

/**
 * App root is the home/landing screen — the one place assistant
 * auto-navigation is allowed (nothing to yank the user away from). The single
 * definition of "home" for the assistant: the context builder, the navigate
 * client tool, and the drawer's auto-fire/chip-suppression logic all key off
 * this so they can never diverge.
 */
function isAssistantHomePath(pathname: string): boolean {
  return pathname === '/';
}

export function buildGlobalAssistantCurrentPageContext(args: {
  pathname: string;
  isInCustomDashEditMode: boolean;
  lastAppliedFilterContext?: LastAppliedFilterContext;
}): GlobalAssistantCurrentPageContext {
  const normalizedPrompt =
    args.lastAppliedFilterContext?.prompt.trim() || undefined;

  if (isAssistantHomePath(args.pathname)) {
    return { pathname: args.pathname, kind: 'home' };
  }

  // The `__std__:` id of the route we are on, when the catalog knows it. This is
  // what lets the loop notice that the page it is about to OFFER is the page
  // already on screen and apply the filters instead.
  const matchedOptionId = getCurrentPageOptionId(args.pathname);
  const matchedOptionIdField = matchedOptionId ? { matchedOptionId } : {};

  if (isDataDictionaryPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'other',
      ...matchedOptionIdField,
    };
  }

  const discovery = getDiscoveryTargetForPath(args.pathname);
  if (discovery) {
    return {
      pathname: args.pathname,
      kind: 'discovery',
      discoveryKind: discovery.discoveryKind,
      ...(normalizedPrompt ? { baseDiscoveryPrompt: normalizedPrompt } : {}),
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
      ...matchedOptionIdField,
    };
  }

  if (args.isInCustomDashEditMode || looksLikeCustomDashPath(args.pathname)) {
    return {
      pathname: args.pathname,
      kind: 'custom_dash',
      ...(normalizedPrompt ? { baseFilterPrompt: normalizedPrompt } : {}),
      // This branch is not only real custom dashboards: every entity STANDARD
      // page (`/company/*` through `/activity/*`) lands here too
      // (looksLikeCustomDashPath keys off the entity segment alone), and those
      // are precisely the pages the same-page rule has to recognise. A genuine
      // custom-dash path finds no catalog entry and stays undefined. Edit mode
      // is excluded: there the custom-dash rules govern, and the path underneath
      // is not what the user is looking at.
      ...(args.isInCustomDashEditMode ? {} : matchedOptionIdField),
    };
  }

  return {
    pathname: args.pathname,
    kind: 'other',
    ...matchedOptionIdField,
  };
}

export function getDiscoveryApplyPrompt(
  match: GlobalAssistantSuggestMatch,
  fallbackPrompt?: string
): string | undefined {
  const override = match.resolvedPromptForApply?.trim();
  if (override) {
    return override;
  }
  const fallback = fallbackPrompt?.trim();
  return fallback || undefined;
}

export function shouldAutoApplyDiscoveryMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      globalAssistantPathsMatch(pathname, match.navigatePath)
  );
}

export function shouldAutoApplyDashboardFilterMatch(
  match: GlobalAssistantSuggestMatch,
  pathname: string
): boolean {
  return Boolean(
    !match.discoveryKind &&
      match.resolvedPromptForApply?.trim() &&
      globalAssistantPathsMatch(pathname, match.navigatePath) &&
      (isCustomDashCurrentPageFilterMatch(match) ||
        isNormalDashboardFilterPath(match.navigatePath))
  );
}

function canApplyFiltersOnCurrentPage(args: {
  pathname: string;
  isInCustomDashEditMode?: boolean;
}): boolean {
  const ctx = buildGlobalAssistantCurrentPageContext({
    pathname: args.pathname,
    isInCustomDashEditMode: args.isInCustomDashEditMode ?? false,
  });
  return (
    ctx.kind === 'custom_dash' || isCurrentPageFilterApplyPath(args.pathname)
  );
}

function matchHasFilterApplyIntent(
  match: GlobalAssistantSuggestMatch
): boolean {
  if (match.applyFiltersAfterNavigate) {
    return true;
  }
  return Boolean(match.resolvedPromptForApply?.trim());
}

export function addCurrentPageFilterApplyOptionForDiscoveryMatch(args: {
  matches: GlobalAssistantSuggestMatch[];
  currentPathname: string;
  prompt?: string;
  isInCustomDashEditMode?: boolean;
}): GlobalAssistantSuggestMatch[] {
  const { matches, currentPathname, prompt, isInCustomDashEditMode } = args;
  if (matches.length !== 1) {
    return matches;
  }
  if (
    !canApplyFiltersOnCurrentPage({
      pathname: currentPathname,
      isInCustomDashEditMode,
    })
  ) {
    return matches;
  }

  const primaryMatch = matches[0];
  if (!primaryMatch || isCurrentPageFilterApplyMatch(primaryMatch)) {
    return matches;
  }
  if (globalAssistantPathsMatch(currentPathname, primaryMatch.navigatePath)) {
    return matches;
  }
  if (!matchHasFilterApplyIntent(primaryMatch)) {
    return matches;
  }

  const promptForApply = getDiscoveryApplyPrompt(primaryMatch, prompt);
  if (!promptForApply) {
    return matches;
  }

  return [
    {
      pageId: `${CURRENT_PAGE_FILTER_APPLY_PAGE_ID_PREFIX}${currentPathname}`,
      name: getCurrentPageFilterApplyLabel(currentPathname),
      navigatePath: currentPathname,
      confidenceScore: primaryMatch.confidenceScore,
      resolvedPromptForApply: promptForApply,
    },
    primaryMatch,
  ];
}

export function hasCurrentPageFilterApplyOption(
  matches: GlobalAssistantSuggestMatch[]
): boolean {
  return matches.some(isCurrentPageFilterApplyMatch);
}

export function isGlobalAssistantCurrentPageFilterable(args: {
  pathname: string;
  isInCustomDashEditMode: boolean;
}): boolean {
  const ctx = buildGlobalAssistantCurrentPageContext({
    pathname: args.pathname,
    isInCustomDashEditMode: args.isInCustomDashEditMode,
  });
  return (
    ctx.kind === 'custom_dash' ||
    ctx.kind === 'discovery' ||
    isCurrentPageFilterApplyPath(args.pathname)
  );
}
